<template>
    <div class="d-flex gap-3">
        <div class="d-flex flex-nowrap">
            <div class="d-flex">
                <ButtonComp
                    data-bs-toggle="button"
                    button-type="Custom"
                    icon="fa-circle-xmark"
                    icon-position="left"
                    @click="errorSelected = !errorSelected"
                    class="btn-xs btn-outline-danger me-1"
                    >{{ $tm('import.errorcount', Object.keys(errorRows).length) }}</ButtonComp
                >

                <ButtonComp
                    data-bs-toggle="button"
                    button-type="Custom"
                    icon="fa-triangle-exclamation"
                    icon-position="left"
                    @click="warningSelected = !warningSelected"
                    class="btn-xs btn-outline-warning mx-1"
                    >{{ $tm('import.warningcount', Object.keys(warningRows).length) }}</ButtonComp
                >

                <ButtonComp
                    data-bs-toggle="button"
                    button-type="Custom"
                    icon="fa-circle-exclamation"
                    icon-position="left"
                    @click="infoSelected = !infoSelected"
                    class="btn-xs btn-outline-info mx-1"
                    >{{ $tm('import.infocount', Object.keys(infoRows).length) }}</ButtonComp
                >
                <span class="ms-2 fst-italic text-nowrap"
                    >{{
                        $ti('import.importingrows', {
                            rows: rows.length - Object.keys(errorRows).length,
                            of: rows.length,
                        })
                    }}
                    <span
                        class="ms-1"
                        v-if="Object.keys(errorRows).length > 0 && ignoreErrors"
                        >{{ $tm('import.ignoreerrorrows', Object.keys(errorRows).length) }}</span
                    >
                </span>
            </div>
        </div>
        <span
            class="ms-3 flex-fill"
            v-if="useFilter"
        >
            <SearchBox
                v-model="searchfilter"
                placeholder="importcomp.filterplaceholder"
            />
        </span>
    </div>
    <GridComp
        ref="grid"
        class="ag-grid-import h-100"
        :class="{ 'ag-hide-pinned': props.lockHeader }"
        :grid-setup="gridSetup"
        style="min-height: 20vh"
        @on-cell-editing-stopped="onCellEditingStopped"
        @grid-ready="onGridReady"
    />
</template>

<script lang="ts" setup generic="TRow">
import { computed, markRaw, ref, type Ref, toRaw, watch } from 'vue';
import { type ComponentExposed } from 'vue-component-type-helpers';
import { ButtonComp, GridActionHeader, GridComp, SearchBox } from '@/components';
import { useDialog } from '@/composeables';
import { $ti, $tm, featureIcons } from '@/.';

import {
    ImportOptions,
    ImportOption,
    ImportValueMap,
    ImportColumnMap,
    ImportValueEditor,
    ImportDetailsRenderer,
    ImportStatusRenderer,
    ImportHeader,
    type IImportValue,
    type IImportStatus,
    type IValidator,
} from './';
import { GridColumnDefinition, GridSetup, GridColumnOptions, GridDataSource } from '../Grid/Classes';
import ContestantImportDetailView from '../Participant/ContestantImportDetailView.vue';
import ImportCellRenderer from './ImportCellRenderer.vue';
import { watchDebounced } from '@vueuse/core';
import { DropdownItemButton, DropdownItemSeparator } from '@/objects';
import type { IBaseObjStaticConstructor } from '@/objects/IBaseObjStaticConstructor';
import type { ISerialize } from '@/objects/ISerialize';

// Define the props
const props = withDefaults(
    defineProps<{
        /**
         * All the options for the import. The map between the database and the header dropdown
         */
        importOptions: ImportOptions<TRow>;

        /**
         * The rows to display in the grid
         */
        rows: any[];

        /**
         * The columns to display in the grid
         */
        columns: any[];

        /**
         * If we should override the column captions or not
         * @requires lockHeader to be true
         * @default null
         */
        columnCaptions?: string[] | null;

        /**
         * The default validators for the columns
         * @default null
         */
        validators?: IValidator[] | null;

        /**
         * If we should hide the header or not.
         * This is just in the GUI and does not affect the data
         * @default false
         */
        lockHeader?: boolean;

        /**
         * If we should show the filter input or not
         * @default false
         */
        useFilter?: boolean;

        /**
         * If we should ignore rows with errors or not
         * @default false
         */
        dontAutoMap?: boolean;

        /**
         * Disable editing of the grid
         * @default false
         */
        readonly?: boolean;
    }>(),
    {
        validators: null,
        lockHeader: false,
        useFilter: false,
        columnCaptions: null,
        dontAutoMap: false,
        readonly: false,
    },
);

const grid = ref<ComponentExposed<typeof GridComp<any>> | null>(null);

const searchfilter = ref<string>('');
const searchFilterDebounced = ref<string>('');

watchDebounced(searchfilter, (nv) => (searchFilterDebounced.value = nv), { debounce: 500 });

// The filter buttons in the GUI
const errorSelected = ref<boolean>(false);
const warningSelected = ref<boolean>(false);
const infoSelected = ref<boolean>(false);

const deletedRows = ref<any[]>([]);

// The rows that have errors, warnings or info
const errorRows = ref<any>({});
const warningRows = ref<any>({});
const infoRows = ref<any>({});

// Internal mapping between the column and the dropdown value
let columnMapping = new Map<string, Ref<ImportOption>>();

/**
 * Get the data rows as a specific type
 * @param type The type of the rows. This is just a typehelper to make sure we have the correct type
 */
function getValuemap<T extends ISerialize>(type: IBaseObjStaticConstructor<T>) {
    // Here we need to convert the rows to ParticipantImportRow format.... and then send it to the server
    // Is this really neccessary? Shouldnt it be enough to only work on the rows and add a mapping between the selected column name and the column name in the row?
    // The benefit of converting it is that we allready here know what field in the database it will be and we can have types on the field instead of just a string
    // The disadvantage is that we need to convert all the data to the correct format before we can send it to the server. But if we dont we would anyway handle the
    // convertion on the server side.
    const map = new ImportValueMap<T>();
    map.Cols = Array.from(columnMapping.values()).map((v) => v.value.text!);

    const ignoreIndexes = Object.keys(errorRows.value).map((key) => parseInt(key));

    rows.value.forEach((row, index) => {
        const rowId = parseInt(row.id);

        // Skip the first row if we have a header
        if (hasHeader.value && rowId == 0) return;

        // We skip all rows with errors
        if (ignoreErrors.value && ignoreIndexes.includes(rowId)) return;

        // Rename all names on the proerties to the correct name and ignore the ones marked as skip
        // We could send in the Type into the component to have a strong type on the row, but is it neccessary?
        let rawRow = toRaw(row);

        // Make sure we dont change the original row
        let newRow: any = {};
        for (const [key, value] of Object.entries(rawRow)) {
            let newKey = columnMapping.get(key);
            // This handles the case when we have a column that is not mapped to anything but are marked as a column that should be included with an @ in the beginning
            // if this key does not exist on the type we provide it will be ignored
            // This also takes precedence over the newKey so if we have a key called Name and a key called @Name the @Name will be used
            if (key.startsWith('@')) {
                newRow[key.substring(1)] = value;
            }
            // If the newKey is not null and not Skip, we should add the value to the new row
            else if (newKey != null && newKey.value.text != 'Skip') {
                newRow[newKey.value.text!] = (value as IImportValue)?.Value;
            }
            // We should always have a ID field in the row
            else if (key.toLowerCase() == 'id') {
                newRow['ID'] = parseInt(value as string); // We need to convert the id to a number
            }
        }
        map.Rows.push(type.FromJsValues(newRow));
    });

    return map;
}

// Property that defines if we have a header row. When this one is set it will skip the first row in the grid and mark it as a pinned row
const hasHeader = ref<boolean>(false);

// Property that defines if we should ignore rows with errors or not
const ignoreErrors = ref<boolean>(false);

// Property that defines if the import is done or not
const importDone = ref<boolean>(false);

/**
 * Set or unset the header row in the grid
 * @param header
 */
const setHasHeader = (header: boolean) => {
    // let oldHeaderValue = hasHeader.value;
    hasHeader.value = header;
    if (header) {
        cleanUpValidation('0', null);
        grid.value?.pinRows(['0'], 'top');
    } else {
        grid.value?.unpinRows('top');
    }
};

/**
 * Will set the import done flag and emit the event
 * @param done
 */
const setImportDone = (done: boolean = true) => {
    importDone.value = done;

    // This should trigger the status colors on the lines
    grid.value?.refreshCachedData();

    emit('importCompleted', done);
};

let columnCallbackTimeout: number | null = null;
let columnCallbackBatch: ImportColumnMap[] = [];

// Define the column callback
// TODO: This is a helper method that I use instead of a deep watch on the ComunMapping object, because that is too slow, and this give each individual change instead.
// Maybe we should find a different way of doing this since we now have one Reactive handle to the columnMapping object and also this callback that contains the same data.
const columnCallback =
    /**
     * Callback for when a column is changed to create a internal mapping
     * @param value
     * @param columnid
     */
    (value: Ref<ImportOption>, columnid: string) => {
        validateCells(rows.value, columnid);

        columnCallbackBatch.push(new ImportColumnMap(columnid, value.value.text!));

        let col = columnMapping.get(columnid);
        if (col) {
            col.value = value.value;
        }

        if (columnCallbackTimeout == null) {
            columnCallbackTimeout = window.setTimeout(() => {
                emit('columnMapChanged', columnCallbackBatch);
                columnCallbackBatch = [];
                columnCallbackTimeout = null;
            }, 200);
        }
    };

/**
 * Contains all editable rows
 * If we have a header, we skip the first row
 */
const rows = computed(() => {
    let rowids: any[] = [];

    if (errorSelected.value) {
        rowids = rowids.concat(Object.keys(errorRows.value));
    }

    if (warningSelected.value) {
        rowids = rowids.concat(Object.keys(warningRows.value));
    }

    if (infoSelected.value) {
        rowids = rowids.concat(Object.keys(infoRows.value));
    }

    let search = searchFilterDebounced.value.toLowerCase();

    // filter the rows based on the errorSelected, warningSelected and infoSelected
    let filteredRows = props.rows.filter((row) => {
        // If deleteRows contains the row, we should not return it
        if (deletedRows.value.includes(row.id)) return false;

        // If we have a search filter, we should filter the rows based on the search filter
        if (search.length > 0) {
            let found = false;
            for (const [key, value] of Object.entries<IImportValue>(row)) {
                if (key.startsWith('_col') && value.Value.toLowerCase().includes(search)) {
                    found = true;
                    break;
                }
            }

            if (!found) return false;
        }

        // if there is no rowids selected and no filters enabled, return all rows
        if (!errorSelected.value && !warningSelected.value && !infoSelected.value) {
            if (hasHeader.value && row.id == '0') {
                return false;
            } else if (rowids.length == 0) {
                return true;
            }
        }

        // return only rows in the filters
        if (rowids.includes(row.id)) {
            return true;
        }

        // dont return rows that are not in the filters
        return false;
    });

    return convertRowsToIImportValue(filteredRows);
});

/**
 * Callback for when the grid is ready
 * Sets up the automap
 */
const onGridReady = () => {
    if (props.validators !== null && props.validators.length > 0) {
        props.validators?.forEach((validator) => {
            updateValidators(validator);
        });
    }
};

/**
 * This one is called when the rows are loaded and will try to automap the header
 * We concider rows loaded when we have more than 0 rows and the previous rows was 0
 */
const onRowsLoaded = () => {
    let header = rows.value.find((row) => row.id == '0');
    let headerValues = [] as string[];

    for (const [key, value] of Object.entries<IImportValue>(header)) {
        if (key.startsWith('_col')) {
            headerValues.push(value.Value?.toLowerCase() ?? '');
        }
    }

    // Update the validators on the options with the new validators
    // We do this so we dont have to revalidate all the cells when we change the validators
    if (props.validators != null) {
        props.validators.forEach((validator) => {
            props.importOptions.options.forEach((option) => {
                if (option.validation != null) {
                    let typename = option.validation?.constructor.name;
                    if (validator.constructor.name == typename) {
                        option.validation = validator;
                    }
                }
                if (option.options) {
                    option.options.forEach((suboption) => {
                        if (suboption.validation != null) {
                            let typename = suboption.validation?.constructor.name;
                            if (validator.constructor.name == typename) {
                                suboption.validation = validator;
                            }
                        }
                    });
                }
            });
        });
    }

    let matchesPct = getHeaderMatch(headerValues);

    setTimeout(() => {
        if (props.dontAutoMap) {
            return;
        }

        if (matchesPct > 0.75) {
            tryAutomapHeader();
        } else if (matchesPct > 0.1) {
            useDialog().YesNo({
                title: $tm('import.headerconfirm'),
                body: $ti('import.headerconfirmtext', { row: headerValues.join(', ') }, true),
                yesCallback: async () => {
                    tryAutomapHeader();
                    return true;
                },
                options: {
                    TextAlign: 'center',
                },
            });
        }
    }, 100);
};

watch(
    () => props.rows,
    (nv, ov) => {
        if (ov.length == 0 && nv.length > 0) {
            onRowsLoaded();
        }
    },
);

/**
 * Callback for when a cell is edited
 * @param row
 * @param column
 */
const onCellEditingStopped = (row: any, column: string) => {
    validateCells([row], column);

    emit('rowchanged', row, column);
};

/**
 * Map the rows to the IImportValue format
 * @param rows
 */
const convertRowsToIImportValue = (rows: any[]) => {
    rows.forEach((row) => {
        for (const [key, value] of Object.entries(row)) {
            if (typeof value == 'string' && key == '_status') {
                row['_status'] = { Status: '', Source: null, Column: null } as IImportStatus;
            } else if (typeof value == 'string' && key != 'id' && key != 'details' && key.substring(0, 1) != '@') {
                row[key] = { Value: value, Status: '', StatusType: 'na', RowId: row.id };
            }
        }
    });

    return rows;
};

/**
 * Contains all pinned rows or header captions
 */
const pinnedrows = computed(() => {
    if (hasHeader.value) {
        return convertRowsToIImportValue(props.rows.filter((row) => row.id == '0'));
    }
    return [];
});

/**
 * Delete the selected rows
 */
const deleteSelected = async () => {
    const selected = grid.value?.getSelectedRows() ?? [];

    if (selected.length == 0) return;

    useDialog().YesNo({
        title: $tm('general.deleteselected'),
        body: $tm('general.deletedselectedmsg', selected?.length),
        yesCallback: async () => {
            selected?.forEach((c) => {
                deletedRows.value.push(c.id);
                cleanUpValidation(c.id, null);
            });

            grid.value?.refreshCachedData();

            return true;
        },
    });
};

const getHeaderMatch = (headerValues: string[]) => {
    if (props.rows.length == 0) return 0;

    let options = props.importOptions.options;

    if (options.length === 0) {
        return 0;
    }

    let automapKeys = [] as string[];
    options.forEach((option) => {
        automapKeys.push(option.text.toLowerCase());
        if (option.automap) {
            automapKeys.push(...option.automap);
        }

        if (option.options) {
            option.options.forEach((suboption) => {
                automapKeys.push(suboption.text.toLowerCase());
                if (suboption.automap) {
                    automapKeys.push(...suboption.automap);
                }
            });
        }
    });

    let matches = headerValues.filter((x) => automapKeys.includes(x));

    return matches.length / headerValues.filter((x) => !x.startsWith('_')).length;
};

/**
 * Try and set the first row as a header row and automap the columns
 */
const tryAutomapHeader = () => {
    if (hasHeader.value || props.rows.length == 0) return;

    setHasHeader(true);
    automapHeader();
};

/**
 * This will try and automap the columns based on the header row
 */
const automapHeader = () => {
    if (!hasHeader.value || props.rows.length == 0) return;

    let options = props.importOptions.options;
    let headerRow = props.rows.find((row) => row.id == '0');

    headerRow['_status'] = { Status: '', Source: null, Column: null } as IImportStatus;

    if (options.length === 0) {
        return;
    }

    // This mean we will set all the values and therefore the isAllColumnsMapped will be set to true.
    // This will trigger the columnMapChanged event for each column when they are beeing mounted
    // this is not optiomal since we will then trigger the event for each column and row in the grid
    for (const [key, importOption] of Object.entries<IImportValue>(headerRow)) {
        let selectedValue = null;

        if (!key.startsWith('_')) {
            continue;
        }

        for (const option of options) {
            if (option.options) {
                for (const subOption of option.options) {
                    selectedValue = automapToOption(subOption, importOption.Value);
                    if (selectedValue) {
                        break;
                    }
                }
                if (selectedValue) {
                    break;
                }
            } else {
                selectedValue = automapToOption(option, importOption.Value);

                if (selectedValue) {
                    break;
                }
            }
        }

        if (selectedValue) {
            let col = columnMapping.get(key);
            if (col) {
                col.value = selectedValue;
            }
        }
    }
};

/**
 * Set the headers based on the options
 * TODO: Do we need a interface for the headers?
 * @param headers
 */
const setHeaders = (headers: { column: string; key: string }[]) => {
    let options = props.importOptions.options;
    headers.forEach((h) => {
        let col = columnMapping.get(h.column);
        if (col) {
            let selectedValue = null;
            for (const option of options) {
                if (option.options) {
                    for (const subOption of option.options) {
                        if (subOption.text == h.key) {
                            selectedValue = subOption;
                            break;
                        }
                    }
                    if (selectedValue) {
                        break;
                    }
                } else {
                    if (option.text == h.key) {
                        selectedValue = option;
                        break;
                    }
                }
            }

            if (selectedValue) {
                col.value = selectedValue;
            }
        }
    });
};

/**
 * Will compare the automap text to the option and return the option if it matches
 * @param option
 * @param automapText
 */
const automapToOption = (option: ImportOption, automapText: string) => {
    if (!automapText || !option.text) return null;

    let automapTextLower = automapText.toLowerCase();

    if (option.text.trim().toLowerCase() == automapTextLower) {
        return option;
    }

    if ($tm(option.value!).toLowerCase() == automapTextLower) {
        return option;
    }

    if (option.automap?.includes(automapTextLower)) {
        return option;
    }
    return null;
};

/**
 * Completely delete a column and data from the import values
 * @param columnid
 */
const columnDeleteCallback = (columnid: string) => {
    grid.value?.removeColumns([columnid]);

    // Remove the id from the columnMapping
    columnMapping.delete(columnid);

    emit('columnDeleted', columnid);
};

/**
 * Define all the colums for the grid
 */
const columns = computed<GridColumnOptions<any>[]>(() => {
    let _columns: GridColumnOptions<any>[] = [];

    _columns.push(
        new GridColumnOptions<any>({
            dataField: 'id',
            editable: false,
            sortable: false,
            filterable: false,
            resizable: false,
            suppressMovable: true,
            maxWidth: 40,
            minWidth: 40,
            headerComponent: GridActionHeader,
            headerComponentParams: {
                params: {},
                dropdownItems: [
                    DropdownItemButton.create({
                        icon: 'fa-square-check',
                        action: () => grid.value?.selectAllRows(),
                        translationkey: 'grid.selectall',
                    }),
                    DropdownItemButton.create({
                        icon: 'fa-wand-magic-sparkles',
                        action: () => tryAutomapHeader(),
                        translationkey: 'import.automap',
                    }),
                    DropdownItemSeparator.create(),
                    DropdownItemButton.create({
                        icon: featureIcons.DELETE,
                        action: () => deleteSelected(),
                        translationkey: 'grid.deleterows',
                    }),
                ],
            },
            cellRenderer: ImportDetailsRenderer,
            cellRendererParams: { view: ContestantImportDetailView },
            valueGetter: (row: any) => {
                return row;
            },
        }),
    );

    props.columns.forEach((col) => {
        if (col == '_status') {
            _columns.push(
                new GridColumnOptions<any>({
                    dataField: col,
                    cellDataType: 'text',
                    columnHeaderText: $tm('import.status'),
                    editable: false,
                    suppressMovable: true,
                    flex: 3,
                    minWidth: 350,
                    cellRenderer: ImportStatusRenderer,
                }),
            );
        } else if (col.startsWith('_col')) {
            //columnMapping.set(col, ref<ImportColumn>(new ImportColumn(props.importOptions.options, columnCallback));

            // if the column ends with a _ we remove it
            let isHidden = false;
            if (col.endsWith('_')) {
                col = col.substring(0, col.length - 1);
                isHidden = false;
            }

            _columns.push(buildColumnDef(col, isHidden));
        }
    });

    return _columns;
});

/**
 * Helper method to build the column definition
 * @param colid
 */
const buildColumnDef = (colid: string, isHidden?: boolean) => {
    const colmap = ref<ImportOption>(props.importOptions.options[0]);
    columnMapping.set(colid, colmap);

    // We try and parse the column index _col1, _col2, _col3 etc
    let colIndex = parseInt(colid.substring(4));
    let columnCaption = props.columnCaptions && props.columnCaptions.length >= colIndex ? props.columnCaptions[colIndex] : undefined;

    return new GridColumnOptions<any>({
        dataField: colid,
        cellDataType: 'text',
        filterable: true,
        hidden: isHidden ?? false,
        cellEditor: ImportValueEditor,
        cellEditorParams: {
            grid: grid,
        },
        cellRenderer: markRaw(ImportCellRenderer),
        editable: (p: any) => !(p.id == 0 && hasHeader.value) && !props.readonly,
        suppressFillHandle: props.readonly, // We should not show the fill handle if the grid is readonly
        minWidth: 100,
        suppressMovable: true,
        valueGetter: (row: any) => {
            return row[colid];
        },
        valueSetter: (row, newValue) => {
            row[colid].Value = newValue;
            return true;
        },

        headerComponent: ImportHeader,
        headerComponentParams: {
            readonly: props.lockHeader,
            options: props.importOptions.options,
            caption: columnCaption,
            params: {
                column: colid,
                value: colmap,
                onValueChange: columnCallback,
                onColumnDelete: columnDeleteCallback,
            },
        },

        filterValues: (pinnedRowIds?: string[]) => {
            let dataset = new Set<string>();
            props.rows.forEach((row) => {
                if (!pinnedRowIds?.includes(row.id)) {
                    try {
                        let cellData = row[colid] as IImportValue;
                        cellData.RowId = 0;
                        dataset.add(JSON.stringify(cellData));
                    } catch {
                        console.error('Error parsing cell data for filtering', row, colid);
                    }
                }
            });

            let value = Array.from(dataset).map((x) => JSON.parse(x));

            let values = new Map<string, any>();
            value.forEach((v) => {
                values.set(v.Value, v);
            });

            return values;
        },

        cellClassRules: [
            {
                className: 'grid-cell-info',
                func: (cellData: IImportValue) => highligtRowOrCell('info', cellData),
            },
            {
                className: 'grid-cell-warning',
                func: (cellData: IImportValue) => highligtRowOrCell('warning', cellData),
            },
            {
                className: 'grid-cell-error',
                func: (cellData: IImportValue) => highligtRowOrCell('error', cellData),
            },
        ],
    });
};

/**
 * Helper method to find the correct status for the cell
 * @param statusType
 * @param data
 */
const highligtRowOrCell = (statusType: 'info' | 'warning' | 'error' | 'na', data: any) => {
    // Check the cell status first
    if (data?.StatusType == statusType) {
        return true;
    }

    return false;
};

/**
 * Validate the content of a cell based on the validators defined on the column
 * @param rows
 * @param column
 */
const validateCells = (rows: any[], column: string) => {
    const v = columnMapping.get(column);

    if (!v) return;

    rows.forEach((row) => {
        if (column != '_status' && !column.startsWith('_col')) return;

        let rowIndex = row.id;
        let cellData: IImportValue = row[column];
        let validator = v!.value?.validation;
        let importStatus = { Status: '', Source: 'validators', Column: v.value.value } as IImportStatus;

        cellData.RowId = rowIndex;

        if (validator) {
            if (!validator.Validate(cellData.Value || '')) {
                // Set the status of the cell
                cellData.StatusType = validator.StatusType();
                cellData.Status = validator.Status();

                importStatus.Status = validator.Status();

                // Add the column to the errorRows
                if (cellData.StatusType == 'error') {
                    addStatus(errorRows.value, rowIndex, column, importStatus);
                    addRowStatus(errorRows.value, row, cellData.Status, v.value.value, 'validators');
                } else if (cellData.StatusType == 'warning') {
                    addStatus(warningRows.value, rowIndex, column, importStatus);
                } else if (cellData.StatusType == 'info') {
                    addStatus(infoRows.value, rowIndex, column, importStatus);
                }
            } else {
                cellData.StatusType = 'na';
                cellData.Status = '';

                cleanUpValidation(rowIndex, v.value, row);
            }
        } else {
            cellData.StatusType = 'na';
            cellData.Status = '';

            cleanUpValidation(rowIndex, v.value, row);
        }
    });

    grid.value?.refreshCells([], [column, '_status']);

    return true;
};

/**
 * Add a status to the statusrows
 * @param statusrows
 * @param rowindex
 * @param column
 * @param status
 */
const addStatus = (statusrows: any[], rowindex: any, column: string, status: IImportStatus) => {
    if (statusrows[rowindex] == undefined) {
        statusrows[rowindex] = {};
    }
    statusrows[rowindex][column] = status;
};

/**
 * Add a status to the row and add it to the correct list
 * @param statusrows
 * @param row
 * @param status
 */
const addRowStatus = (
    statusrows: any[],
    row: any,
    status: string | null = null,
    column: string | null = null,
    source: 'validators' | 'external' = 'validators',
): boolean => {
    let rowindex = row.id;

    // If we are trying to clear a value that is not set, we should return false
    if (status == null && statusrows[rowindex] == undefined) {
        return false;
    }

    if (statusrows[rowindex] == undefined) {
        statusrows[rowindex] = {};
    }

    let rowstatus = { Status: status, Source: source, Column: column } as IImportStatus;

    // Set the status on the row in the datasource.
    let currentRowStatus = row['_status'] as IImportStatus;

    // We only want to set the status if the source is validators or if the current status is the same source as the new status
    if (rowstatus.Source == 'validators' || (currentRowStatus?.Source ?? rowstatus.Source) == rowstatus.Source) {
        row['_status'] = rowstatus;

        if (status == null && statusrows[rowindex] != undefined && (statusrows[rowindex]['_status'] ?? null == null)) {
            delete statusrows[rowindex]['_status'];
        } else {
            statusrows[rowindex]['_status'] = rowstatus;
        }

        if (Object.keys(statusrows[rowindex]).length == 0) {
            delete statusrows[rowindex];
        }
    }

    return true;
};

/**
 * Helper function to clean up the status
 * @param statusrows
 * @param rowindex
 * @param option
 * @param row
 */
const cleanUpStatus = (statusrows: any[], rowindex: any, option: ImportOption, row: any | null) => {
    if (statusrows[rowindex] && statusrows[rowindex][option?.column!]) {
        delete statusrows[rowindex][option?.column!];
        // if the row has another _col status we should move it to the row _status
        for (const [key, value] of Object.entries<IImportStatus>(statusrows[rowindex])) {
            if (key.startsWith('_col')) {
                statusrows[rowindex]['_status'] = value;
                if (row != null) {
                    row['_status'] = value;
                }
                break;
            }
        }
    }

    let emptyLength = statusrows[rowindex] != undefined && statusrows[rowindex]['_status'] != undefined ? 1 : 0;

    if (statusrows[rowindex] && Object.keys(statusrows[rowindex]).length == emptyLength) {
        row['_status'] = { Status: '', Source: null } as IImportStatus;
        delete statusrows[rowindex];
    }
};

/**
 * Helper function to clean up the validation
 * @param rowIndex
 * @param option
 * @param row if provided, we will also set the correct status on the row based on the columns
 */
const cleanUpValidation = (rowIndex: any, option: ImportOption | null, row: any | null = null) => {
    if (option?.column == null) {
        // We assume that we should remove all statuses for the row

        if (errorRows.value[rowIndex] != undefined) {
            delete errorRows.value[rowIndex];
        }
        if (warningRows.value[rowIndex] != undefined) {
            delete warningRows.value[rowIndex];
        }
        if (infoRows.value[rowIndex] != undefined) {
            delete infoRows.value[rowIndex];
        }
    } else {
        cleanUpStatus(infoRows.value, rowIndex, option, row);
        cleanUpStatus(warningRows.value, rowIndex, option, row);
        cleanUpStatus(errorRows.value, rowIndex, option, row);
    }
};

/**
 * Define the datasource for the grid
 */
const dataSource = new GridDataSource<any>({
    rowGetter: (p) => {
        let filtered = p.filter(rows.value);

        const slice = filtered.slice(p.startRow, p.endRow);
        return p.successCallback(slice, filtered, filtered.length);
    },
    pinnedTopRowData: pinnedrows.value,
});

/**
 * Define the grid setup
 */
const gridSetup = new GridSetup<any>({
    datasource: dataSource,
    hasCheckboxSelectionColumn: true,
    rowSelection: 'multiple',
    GridColumnDefinition: new GridColumnDefinition({ ColumnOptions: columns.value }),
    rowIdGetter: (data: any) => data.id,
    rowClassRules: [
        {
            className: 'grid-row-info',
            func: (data) => {
                return (
                    errorRows.value[data.id] === undefined &&
                    warningRows.value[data.id] === undefined &&
                    infoRows.value[data.id] !== undefined
                );
            },
        },
        {
            className: 'grid-row-warning',
            func: (data) => {
                return errorRows.value[data.id] === undefined && warningRows.value[data.id] !== undefined;
            },
        },
        {
            className: 'grid-row-error',
            func: (data) => {
                return errorRows.value[data.id] !== undefined;
            },
        },
        {
            className: 'grid-row-success',
            func: (data) => {
                return importDone.value && errorRows.value[data.id] === undefined && warningRows.value[data.id] === undefined;
            },
        },
    ],
});

/**
 * Emit events
 */
const emit = defineEmits<{
    (e: 'processed', data: TRow[]): void;
    (e: 'rowchanged', row: TRow, column: string): void;
    (e: 'columnMapChanged', data: ImportColumnMap[]): void;
    (e: 'hasErrors', hasErrors: boolean): void;
    (e: 'importCompleted', done: boolean): void;
    (e: 'columnDeleted', column: string): void;
}>();

/**
 * Watch the errorRows and emit the hasErrors event
 */
watch(
    () => errorRows.value,
    () => emit('hasErrors', Object.keys(errorRows.value).length > 0),
    { deep: true },
);

/**
 * This basically watches when the length of the rows changes and refreshes the grid
 * This is used when we apply our filters to the grid.
 */
watch(
    () => rows.value,
    () => grid.value?.refreshCachedData(),
);

/**
 * This will update all validators of the same type as the provided one
 * @param validator
 */
const updateValidators = (validator: IValidator) => {
    columnMapping.forEach((value: Ref<ImportOption>, field: string) => {
        let typename = value.value.validation?.constructor.name;
        if (validator.constructor.name == typename) {
            value.value.validation = validator;

            validateCells(rows.value, field);
        }
    });
};

/**
 * Expose the UpdateValidators function
 */
defineExpose({
    /**
     * This will update all validators of the same type as the provided one
     * @param validator
     */
    UpdateValidators: updateValidators,

    /**
     * Get the value map of the rows including both the columns and the rows
     */
    GetValuemap: getValuemap,

    /**
     * Set the row info
     * @param row
     * @param status
     */
    SetRowInfo(row: any, status: string | null = null) {
        addRowStatus(infoRows.value, row, status, null, 'external');
    },

    /**
     * Set the row warnings
     * @param row
     * @param status
     */
    SetRowWarning(row: any, status: string | null = null) {
        addRowStatus(warningRows.value, row, status, null, 'external');
    },

    /**
     * Set the row errors
     * @param row
     * @param status
     */
    SetRowError(row: any, status: string | null = null) {
        addRowStatus(errorRows.value, row, status, null, 'external');
    },

    /**
     * Refresh the rows in the grid
     * @param rows
     */
    RefreshRows(rows: [] | null = null) {
        grid.value?.refreshCachedData();

        grid.value?.refreshCells(undefined, ['id', '_status']);
    },

    /**
     * Gets the header status
     */
    HasHeader() {
        return hasHeader.value;
    },

    SetHasHeader(header: boolean) {
        setHasHeader(header);
    },

    /**
     * Set the ignore errors flag
     * @param ignore
     */
    SetIgnoreErrors(ignore: boolean) {
        ignoreErrors.value = ignore;
    },

    /**
     * Sets the header options
     */
    SetHeaders(headers: { column: string; key: string }[]) {
        setHeaders(headers);
    },

    /**
     * Resize the columns to fit the content
     */
    ResizeColumnsToFitContent() {
        grid.value?.resizeColumns(true);
    },

    /**
     * Clear all statuses
     */
    ClearAllStatuses() {
        errorRows.value = {};
        warningRows.value = {};
        infoRows.value = {};

        rows.value.forEach((row) => {
            row['_status'] = { Status: '', Source: null } as IImportStatus;
        });
    },

    /**
     * Set the import done flag
     */
    ImportData() {
        setImportDone();
    },
});
</script>
